import TextInput from "../../../inputs/text-input";
import { Content, InputContainer, Label, Subscription } from "../styles";
import { ControllerRenderProps } from "react-hook-form";
import {
  ApprovalDetailInformationEntity,
  ApprovalEntity
} from "../../../../generated/graphql";
import { IApprovalFormFieldValue } from "../types";
import { useContext, useEffect, useMemo } from "react";
import { Name } from "../../../../screens/approval-management/approval-settings/open-windows/styles";
import Button from "../../../globalComponents/Button";
import { Icon } from "@iconify/react";
import PlusIcon from "@iconify-icons/cil/plus";
import styled from "styled-components";
import { colors } from "../../../GlobalStyle/GlobalStyle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import CheckBoxUi from "../../../globalComponents/CheckBoxUi";
import React from "react";
import ApprovalDetailPopupStore from "../../approval-detail-popup-store.ctx";
interface DateTimeFieldsProps {
  isChangeWorkingTimeForm: boolean;
  isRequest?: boolean;
  template?: any;
  approvalDetailInformation?: ApprovalDetailInformationEntity;
  data?: ApprovalEntity;
  isActiveTime: boolean;
  startDate: ControllerRenderProps<IApprovalFormFieldValue, "startDate">;
  endDate: ControllerRenderProps<IApprovalFormFieldValue, "endDate">;
  startTime: ControllerRenderProps<IApprovalFormFieldValue, "startTime">;
  endTime: ControllerRenderProps<IApprovalFormFieldValue, "endTime">;
  isTemporaryStorage: boolean;
}

export interface IAdjustTime {
  date: string;
  useStartTime: number;
  useEndTime: number;
  startTime: string;
  endTime: string;
}

interface ICommuteJson {
  apply_date: string;
  in_datetime_use: number;
  out_datetime_use: number;
  in_datetime: string;
  out_datetime: string;
}

const StyledComponents = {
  Container: styled.div`
    display: flex;
    flex: 1;
    align-items: center;
  `,
  ButtonContent: styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: fit-content;
    gap: 5px;
  `,
  Button: styled(Button)`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  BinButton: styled(FontAwesomeIcon)`
    cursor: pointer;
    color: ${props => props.theme.colors.darkRed};
    margin-right: 10px;
    :hover {
      color: ${props => props.theme.colors.lightRed};
      transform: scale(1.5);
    }
  `,
  MarginTextInput: styled(TextInput)`
    margin-left: 10px;
    margin-right: 10px;
  `
};

const DEFAULT_TIME = {
  date: moment().format("YYYY-MM-DD"),
  useStartTime: 0,
  useEndTime: 0,
  startTime: "09:00:00",
  endTime: "18:00:00"
};

const DateTimeFields = ({
  isChangeWorkingTimeForm,
  isRequest,
  template,
  approvalDetailInformation,
  data,
  isActiveTime,
  startDate,
  endDate,
  startTime,
  endTime,
  isTemporaryStorage
}: DateTimeFieldsProps) => {
  const contextStore = useContext(ApprovalDetailPopupStore);

  // If context is undefined, provide fallback empty array
  const adjustTimes = useMemo(() => {
    return contextStore?.adjustTimes || [];
  }, [isRequest, contextStore?.adjustTimes]);

  const isEditable = isRequest || isTemporaryStorage;

  const setAdjustTimes = (newAdjustTimes: IAdjustTime[]) => {
    if (contextStore?.setAdjustTimes) {
      contextStore.setAdjustTimes(newAdjustTimes);
    }
  };

  const handleAddAdjustTime = () => {
    setAdjustTimes([...adjustTimes, { ...DEFAULT_TIME }]);
  };

  const handleDeleteAdjustTime = (indexToRemove: number) => {
    setAdjustTimes(
      adjustTimes.filter(
        (_: IAdjustTime, index: number) => index !== indexToRemove
      )
    );
  };

  const updateAdjustTime = (
    index: number,
    field: keyof IAdjustTime,
    value: any
  ) => {
    setAdjustTimes(
      adjustTimes.map((time: IAdjustTime, i: number) =>
        i === index ? { ...time, [field]: value } : time
      )
    );
  };

  useEffect(() => {
    setAdjustTimes(
      JSON.parse(approvalDetailInformation?.commuteJson ?? "[]").map(
        (item: ICommuteJson) => ({
          date: item.apply_date,
          useStartTime: item.in_datetime_use,
          useEndTime: item.out_datetime_use,
          startTime: item.in_datetime,
          endTime: item.out_datetime
        })
      )
    );
  }, [approvalDetailInformation?.commuteJson]);

  const getTemplateTitle = () =>
    template?.templateTitle ?? approvalDetailInformation?.formTemplateTitle;

  const renderAdjustTimeForm = () => (
    <>
      <Content style={{ gridColumn: "span 3" }}>
        <Label>관리이름</Label>
        <Subscription>{getTemplateTitle()}</Subscription>
      </Content>
      {adjustTimes.map((adjustTime: IAdjustTime, index: number) => {
        return (
          <React.Fragment key={index}>
            <Content>
              <Label>
                {isEditable && (
                  <StyledComponents.BinButton
                    onClick={() => handleDeleteAdjustTime(index)}
                    icon={faTrash}
                  />
                )}
                조정일자
              </Label>
              <InputContainer>
                <StyledComponents.MarginTextInput
                  disabled={!isEditable}
                  type="date"
                  value={adjustTime.date}
                  onChange={e =>
                    updateAdjustTime(index, "date", e.target.value)
                  }
                />
              </InputContainer>
            </Content>
            <Content>
              <Label>
                <CheckBoxUi
                  disabled={!isEditable}
                  checked={adjustTime.useStartTime === 1}
                  htmlFor={"useStartTime"}
                  onChange={e =>
                    updateAdjustTime(
                      index,
                      "useStartTime",
                      e.target.checked ? 1 : 0
                    )
                  }
                  title=""
                />
                출근시간
              </Label>
              <InputContainer>
                <StyledComponents.MarginTextInput
                  disabled={!isEditable || adjustTime.useStartTime === 0}
                  type="time"
                  value={adjustTime.startTime}
                  onChange={e =>
                    updateAdjustTime(index, "startTime", e.target.value)
                  }
                />
              </InputContainer>
            </Content>
            <Content>
              <Label>
                <CheckBoxUi
                  disabled={!isEditable}
                  checked={adjustTime.useEndTime === 1}
                  htmlFor={"useEndTime"}
                  onChange={e =>
                    updateAdjustTime(
                      index,
                      "useEndTime",
                      e.target.checked ? 1 : 0
                    )
                  }
                  title=""
                />
                퇴근시간
              </Label>
              <InputContainer>
                <StyledComponents.MarginTextInput
                  disabled={!isEditable || adjustTime.useEndTime === 0}
                  type="time"
                  value={adjustTime.endTime}
                  onChange={e =>
                    updateAdjustTime(index, "endTime", e.target.value)
                  }
                />
              </InputContainer>
            </Content>
          </React.Fragment>
        );
      })}
      {isEditable && (
        <StyledComponents.Container>
          <StyledComponents.Button
            backgroundColor={colors.darkBlue}
            hoverBackgroundColor={colors.skyBlue}
            onClick={handleAddAdjustTime}
          >
            <StyledComponents.ButtonContent>
              <Icon icon={PlusIcon} />
              <Name>조정일자추가</Name>
            </StyledComponents.ButtonContent>
          </StyledComponents.Button>
        </StyledComponents.Container>
      )}
    </>
  );

  const renderDefaultForm = () => (
    <>
      <Content style={{ gridRow: "span 2" }}>
        <Label>관리이름</Label>
        <Subscription>{getTemplateTitle()}</Subscription>
      </Content>
      <Content>
        <Label>시작일자</Label>
        {isEditable ? (
          <InputContainer>
            <TextInput type="date" {...startDate} />
          </InputContainer>
        ) : (
          <Subscription>{data?.extendStartDate}</Subscription>
        )}
      </Content>
      <Content>
        <Label>종료일자</Label>
        {isEditable ? (
          <InputContainer>
            <TextInput type="date" {...endDate} />
          </InputContainer>
        ) : (
          <Subscription>{data?.extendEndDate}</Subscription>
        )}
      </Content>
      <Content>
        <Label>시작시간</Label>
        <Subscription>
          {data?.extendStartDateTime ?? (
            <TextInput type="time" {...startTime} disabled={isActiveTime} />
          )}
        </Subscription>
      </Content>
      <Content>
        <Label>종료시간</Label>
        <Subscription>
          {data?.extendEndDateTime ?? (
            <TextInput type="time" {...endTime} disabled={isActiveTime} />
          )}
        </Subscription>
      </Content>
    </>
  );

  return isChangeWorkingTimeForm ? renderAdjustTimeForm() : renderDefaultForm();
};

export default DateTimeFields;
